import {
  type CollectionFields,
  type CollectionName,
  collectionNames,
  type Mutation,
  type PullResponse,
  type PushResponse,
  validatePatch,
  validateRecord,
} from '@dovefiniscono/shared';
import { uuidv7 } from 'uuidv7';
import type { Entry, EntryKey, LocalRecord, StoreChange } from './types';

const META_KEYS = new Set(['id', 'version', 'createdAt', 'updatedAt', 'deletedAt']);

/** A local write the shared schemas refuse; `message` is ready to show (Italian). */
export class RecordValidationError extends Error {
  readonly field: string | undefined;
  constructor(message: string, field?: string) {
    super(message);
    this.name = 'RecordValidationError';
    this.field = field;
  }
}

export type SentMutation = EntryKey & { rev: string };

type ChangeListener = (change: StoreChange) => void;

function fieldsOf(record: LocalRecord): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (!META_KEYS.has(key)) fields[key] = value;
  }
  return fields;
}

function pick(record: LocalRecord, keys: string[]): Record<string, unknown> {
  const source = record as unknown as Record<string, unknown>;
  return Object.fromEntries(keys.filter((key) => key in source).map((key) => [key, source[key]]));
}

function failure(error: { issues: { message: string; path: PropertyKey[] }[] }) {
  const issue = error.issues[0];
  return new RecordValidationError(
    issue?.message ?? 'Dati non validi.',
    issue?.path[0] === undefined ? undefined : String(issue.path[0]),
  );
}

/**
 * In-memory copy of the user's data. The UI reads and writes only here: writes are instant,
 * marked as pending and sent to the server by the SyncEngine.
 */
export class SyncStore {
  #entries = new Map<CollectionName, Map<string, Entry>>(
    collectionNames.map((name) => [name, new Map()]),
  );
  #cursor = 0;
  #snapshots = new Map<CollectionName, readonly LocalRecord[]>();
  #listeners = new Set<() => void>();
  #changeListeners = new Set<ChangeListener>();
  readonly #now: () => Date;
  readonly #newId: () => string;

  constructor(options: { now?: () => Date; newId?: () => string } = {}) {
    this.#now = options.now ?? (() => new Date());
    this.#newId = options.newId ?? uuidv7;
  }

  /** Last server version applied. */
  get cursor(): number {
    return this.#cursor;
  }

  // ── Reading ──────────────────────────────────────────────────────────────

  /** Live records of a collection. The same array is returned until the collection changes. */
  list<C extends CollectionName>(collection: C): readonly LocalRecord<C>[] {
    let snapshot = this.#snapshots.get(collection);
    if (!snapshot) {
      snapshot = [...this.#map(collection).values()]
        .filter((entry) => !entry.pending?.deleted)
        .map((entry) => entry.record);
      this.#snapshots.set(collection, snapshot);
    }
    return snapshot as unknown as readonly LocalRecord<C>[];
  }

  get<C extends CollectionName>(collection: C, id: string): LocalRecord<C> | undefined {
    const entry = this.#map(collection).get(id);
    return entry && !entry.pending?.deleted
      ? (entry.record as unknown as LocalRecord<C>)
      : undefined;
  }

  pendingCount(): number {
    let count = 0;
    for (const map of this.#entries.values()) {
      for (const entry of map.values()) if (entry.pending) count++;
    }
    return count;
  }

  /** For React's useSyncExternalStore. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  /** Every change to persist or share with other tabs (not the ones received from them). */
  onChange(listener: ChangeListener): () => void {
    this.#changeListeners.add(listener);
    return () => this.#changeListeners.delete(listener);
  }

  // ── Local writes ─────────────────────────────────────────────────────────

  /**
   * A new record. With `id`, a record every device makes the same way (a recurring copy, a rule
   * applied): if it is already here it is left as it is.
   */
  create<C extends CollectionName>(
    collection: C,
    fields: CollectionFields[C],
    id?: string,
  ): LocalRecord<C> {
    if (id !== undefined) {
      const existing = this.#map(collection).get(id);
      if (existing) return existing.record as unknown as LocalRecord<C>;
    }
    const result = validateRecord(collection, fields);
    if (!result.success) throw failure(result.error);
    const now = this.#now().toISOString();
    const record = {
      ...result.data,
      id: id ?? this.#newId(),
      version: 0,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
    } as LocalRecord<C>;
    const entry: Entry<C> = {
      collection,
      record,
      pending: { fields: Object.keys(result.data), deleted: false, rev: this.#newId() },
    };
    this.#commit({ put: [entry as Entry], remove: [] });
    return record;
  }

  update<C extends CollectionName>(
    collection: C,
    id: string,
    patch: Partial<CollectionFields[C]>,
  ): LocalRecord<C> {
    const entry = this.#map(collection).get(id);
    if (!entry || entry.pending?.deleted) throw new RecordValidationError('Elemento non trovato.');
    const parsed = validatePatch(collection, patch);
    if (!parsed.success) throw failure(parsed.error);
    const merged = validateRecord(collection, { ...fieldsOf(entry.record), ...parsed.data });
    if (!merged.success) throw failure(merged.error);

    const record = {
      ...entry.record,
      ...parsed.data,
      updatedAt: this.#now().toISOString(),
    } as unknown as LocalRecord<C>;
    const fields = new Set([...(entry.pending?.fields ?? []), ...Object.keys(parsed.data)]);
    this.#commit({
      put: [
        {
          collection,
          record,
          pending: { fields: [...fields], deleted: false, rev: this.#newId() },
        },
      ],
      remove: [],
    });
    return record;
  }

  /** Hidden at once; removed for good when the server confirms the deletion. */
  remove(collection: CollectionName, id: string): void {
    const entry = this.#map(collection).get(id);
    if (!entry || entry.pending?.deleted) return;
    this.#commit({
      put: [
        {
          ...entry,
          pending: { fields: entry.pending?.fields ?? [], deleted: true, rev: this.#newId() },
        },
      ],
      remove: [],
    });
  }

  // ── Sync plumbing (used by SyncEngine) ───────────────────────────────────

  /** Loads persisted data at startup, without notifying anyone. */
  hydrate(data: { cursor: number; entries: Entry[] }): void {
    for (const entry of data.entries) this.#map(entry.collection).set(entry.record.id, entry);
    this.#cursor = data.cursor;
    this.#snapshots.clear();
    this.#notify();
  }

  /** Pending changes as push mutations, oldest collections first. */
  pendingMutations(limit: number): { mutations: Mutation[]; sent: SentMutation[] } {
    const mutations: Mutation[] = [];
    const sent: SentMutation[] = [];
    for (const [collection, map] of this.#entries) {
      for (const entry of map.values()) {
        if (!entry.pending) continue;
        if (mutations.length >= limit) return { mutations, sent };
        const { id } = entry.record;
        if (entry.pending.deleted) {
          mutations.push({ op: 'delete', collection, id });
        } else {
          // New records travel whole; known ones only with the edited fields.
          const fields =
            entry.record.version === 0
              ? fieldsOf(entry.record)
              : pick(entry.record, entry.pending.fields);
          mutations.push({ op: 'upsert', collection, id, fields });
        }
        sent.push({ collection, id, rev: entry.pending.rev });
      }
    }
    return { mutations, sent };
  }

  /**
   * Marks pushed changes as confirmed, unless they were edited again meanwhile (they stay
   * pending and go with the next push). Returns true if the server refused records it already
   * had: the local copy may now differ, so everything must be pulled again.
   */
  acknowledge(sent: SentMutation[], response: PushResponse): { refetch: boolean } {
    const rejected = new Set(response.rejected.map((r) => `${r.collection}:${r.id}`));
    const change: StoreChange = { put: [], remove: [] };
    let refetch = false;

    for (const item of sent) {
      const entry = this.#map(item.collection).get(item.id);
      if (!entry?.pending) continue;
      const key = { collection: item.collection, id: item.id };
      if (rejected.has(`${item.collection}:${item.id}`)) {
        if (entry.record.version > 0) {
          refetch = true;
          change.put.push({ ...entry, pending: null });
        } else {
          change.remove.push(key);
        }
        continue;
      }
      if (entry.pending.rev !== item.rev) continue;
      if (entry.pending.deleted) change.remove.push(key);
      else
        change.put.push({
          ...entry,
          record: { ...entry.record, version: response.version },
          pending: null,
        });
    }
    this.#commit(change);
    return { refetch };
  }

  /**
   * Applies what the server sent. Deletions always win; on records edited locally, the edited
   * fields keep their local value until pushed.
   */
  applyPull(response: PullResponse): void {
    const change: StoreChange = { put: [], remove: [], cursor: response.version };

    if (response.reset) {
      for (const [collection, map] of this.#entries) {
        for (const entry of map.values()) {
          if (!entry.pending) change.remove.push({ collection, id: entry.record.id });
        }
      }
    }

    for (const [name, records] of Object.entries(response.changes)) {
      const collection = name as CollectionName;
      for (const raw of records ?? []) {
        const incoming = raw as unknown as LocalRecord;
        const key = { collection, id: incoming.id };
        const local = this.#map(collection).get(incoming.id);
        if (incoming.deletedAt) {
          if (local) change.remove.push(key);
          continue;
        }
        if (local?.pending?.deleted) continue;
        const record = local?.pending
          ? ({ ...incoming, ...pick(local.record, local.pending.fields) } as LocalRecord)
          : incoming;
        // Removals are applied before puts, so a reset followed by this put keeps the record.
        change.put.push({ collection, record, pending: local?.pending ?? null });
      }
    }
    this.#commit(change);
  }

  /** Server version reached by our own push when nobody else wrote in between. */
  advanceCursor(version: number): void {
    if (version > this.#cursor) this.#commit({ put: [], remove: [], cursor: version });
  }

  /** Forces the next pull to start from scratch. */
  resetCursor(): void {
    this.#commit({ put: [], remove: [], cursor: 0 });
  }

  /** A change made by another tab: it already persisted it. */
  applyExternal(change: StoreChange): void {
    this.#apply(change);
  }

  // ── Internals ────────────────────────────────────────────────────────────

  #map<C extends CollectionName>(collection: C): Map<string, Entry> {
    const map = this.#entries.get(collection);
    if (!map) throw new Error(`Unknown collection ${collection}`);
    return map;
  }

  #commit(change: StoreChange): void {
    if (change.put.length === 0 && change.remove.length === 0 && change.cursor === undefined) {
      return;
    }
    this.#apply(change);
    for (const listener of this.#changeListeners) listener(change);
  }

  #apply(change: StoreChange): void {
    for (const { collection, id } of change.remove) {
      this.#map(collection).delete(id);
      this.#snapshots.delete(collection);
    }
    for (const entry of change.put) {
      this.#map(entry.collection).set(entry.record.id, entry);
      this.#snapshots.delete(entry.collection);
    }
    if (change.cursor !== undefined) this.#cursor = change.cursor;
    if (change.put.length > 0 || change.remove.length > 0) this.#notify();
  }

  #notify(): void {
    for (const listener of this.#listeners) listener();
  }
}
